import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { PagoCombustibleVehiculosViewModel } from "src/app/_core/models/centros/cebe-contract-list-base.model";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
    selector: "app-pago-combustible-vehiculos-autocomplete",
    templateUrl: "./pago-combustible-vehiculos-autocomplete.component.html",
    styleUrls: ["./pago-combustible-vehiculos-autocomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PagoCombustibleVehiculosAutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class PagoCombustibleVehiculosAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Pago de Combustible Vehículos";
	@Input() placeholder =
		"Buscar pago de combustible vehículos por código o descripción";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint =
		"Busque por código o descripción de pago de combustible vehículos";

	// Outputs
	@Output() pagoCombustibleVehiculosSelected =
		new EventEmitter<PagoCombustibleVehiculosViewModel>();

	// State
	pagosCombustibleVehiculos: PagoCombustibleVehiculosViewModel[] = [];
	pagoCombustibleVehiculosId: number | null = null;
	selectedPagoCombustibleVehiculos: PagoCombustibleVehiculosViewModel | null =
		null;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadPagosCombustibleVehiculos();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.pagoCombustibleVehiculosId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Carga la lista de pagos de combustible vehículos
	 */
	private loadPagosCombustibleVehiculos(): void {
		this.masterService
			.getPagoCombustibleVehiculosList()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (pagosCombustibleVehiculos) => {
					this.pagosCombustibleVehiculos = pagosCombustibleVehiculos;
					this.syncSelected();
					this.cdr.markForCheck();
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar los pagos de combustible vehículos",
					);
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedPagoCombustibleVehiculos =
			this.pagoCombustibleVehiculosId != null
				? (this.pagosCombustibleVehiculos.find(
						(p) => p.id === this.pagoCombustibleVehiculosId,
					) ?? null)
				: null;
	}

	/**
	 * Obtiene el texto mostrado para un pago de combustible vehículos
	 */
	displayPagoCombustibleVehiculos = (
		pagoCombustibleVehiculos: PagoCombustibleVehiculosViewModel | string,
	): string => {
		if (typeof pagoCombustibleVehiculos === "string") {
			return pagoCombustibleVehiculos;
		}
		return pagoCombustibleVehiculos &&
			pagoCombustibleVehiculos.codigo &&
			pagoCombustibleVehiculos.descripcion
			? `${pagoCombustibleVehiculos.codigo} - ${pagoCombustibleVehiculos.descripcion}`
			: "";
	};

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(
		pagoCombustibleVehiculos: PagoCombustibleVehiculosViewModel | null,
	): void {
		this.pagoCombustibleVehiculosId = pagoCombustibleVehiculos
			? pagoCombustibleVehiculos.id
			: null;
		this.selectedPagoCombustibleVehiculos = pagoCombustibleVehiculos;
		this.onChange(this.pagoCombustibleVehiculosId);
		this.onTouched();
		if (pagoCombustibleVehiculos) {
			this.pagoCombustibleVehiculosSelected.emit(pagoCombustibleVehiculos);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}
}
